import { lazy } from 'react';

// material-ui
import { Typography, Tooltip } from '@mui/material';

// third-party
import { format, parseJSON } from 'date-fns';

// project imports
import { appState, appGridActions } from 'store/behaviors/loader';
import capabilities from './capabilities';
import { WORKMGMT_ENTITY_TYPE_MAX_DEPTH } from '../entity-type-tree';
import { renderUser } from 'ui-component/fields/EntitySelect/admin/UserSelect';

// assets
import AddCircleTwoToneIcon from '@mui/icons-material/AddCircleTwoTone';

// lazy loaded components
const UserSelect = lazy(() => import('ui-component/fields/EntitySelect/admin/UserSelect'));

const initialState = {
    ...appState(capabilities),
    colDef: [
        {
            field: 'actions',
            type: 'actions',
            description: '',
            align: 'center',
            hideable: false,
            sortable: false,
            resizable: false,
            disableReorder: true,
            label: 'ui-comp-table-actions',
            getActions: (params, permissionData, onClickActions) => {
                const gridActions = appGridActions(capabilities, params?.row, permissionData, onClickActions);

                permissionData?.forEach((perm) => {
                    switch (perm) {
                        case 'canAdd':
                            if (
                                params?.row?.deletedAt ||
                                params?.row?.treeDepth >= WORKMGMT_ENTITY_TYPE_MAX_DEPTH - 1 ||
                                typeof onClickActions?.onClickRowActionAddSubType !== 'function'
                            ) {
                                return;
                            }

                            gridActions.push({
                                key: `actionaddsubtype-row${params?.row?.id || params?.row?.treeNodeId}`,
                                icon: <AddCircleTwoToneIcon fontSize="small" />,
                                label: 'ui-comp-button-add',
                                position: 0,
                                showInMenu: false,
                                onClick: () => onClickActions.onClickRowActionAddSubType(params?.row)
                            });
                            break;
                        default:
                    }
                });

                return gridActions;
            }
        },
        {
            field: 'id',
            type: 'number',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            label: 'traits-appentity-id'
        },
        {
            field: 'acronym',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            label: 'entity-workmgmt-businessopportunitytype-acronym'
        },
        {
            field: 'name',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            flex: 1,
            label: 'traits-appentityisdescribable-name'
        },
        {
            field: 'description',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: false,
            resizable: true,
            exportable: true,
            label: 'traits-appentityisdescribable-description'
        },
        {
            field: 'businessOpportunityTplCount',
            type: 'number',
            description: '',
            align: 'center',
            hideable: true,
            sortable: false,
            resizable: true,
            exportable: true,
            label: 'entity-tplmgmt-businessopportunity-plural'
        },
        {
            field: 'businessOpportunityCount',
            type: 'number',
            description: '',
            align: 'center',
            hideable: true,
            sortable: false,
            resizable: true,
            exportable: true,
            label: 'entity-workmgmt-businessopportunity-plural'
        },
        {
            field: 'createdAt',
            type: 'dateTime',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            valueGetter: (params) => params?.value && parseJSON(params.value),
            renderCell: (params) =>
                params?.value && (
                    <Tooltip title={format(new Date(params.value), 'd/MM/yyyy HH:mm')}>
                        <Typography
                            variant="inherit"
                            color="inherit"
                            sx={{
                                overflow: 'hidden',
                                display: 'block',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {format(new Date(params.value), 'd/MM/yyyy HH:mm')}
                        </Typography>
                    </Tooltip>
                ),
            label: 'traits-appentity-createdat'
        },
        {
            field: 'createdBy',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            renderCell: (params) => renderUser(params?.value, 'fullName'),
            label: 'traits-appentity-createdby'
        },
        {
            field: 'updatedAt',
            type: 'dateTime',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            valueGetter: (params) => params?.value && parseJSON(params.value),
            renderCell: (params) =>
                params?.value && (
                    <Tooltip title={format(new Date(params.value), 'd/MM/yyyy HH:mm')}>
                        <Typography
                            variant="inherit"
                            color="inherit"
                            sx={{
                                overflow: 'hidden',
                                display: 'block',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {format(new Date(params.value), 'd/MM/yyyy HH:mm')}
                        </Typography>
                    </Tooltip>
                ),
            label: 'traits-appentity-updatedat'
        },
        {
            field: 'updatedBy',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            renderCell: (params) => renderUser(params?.value, 'fullName'),
            label: 'traits-appentity-updatedby'
        },
        {
            field: 'deletedAt',
            type: 'dateTime',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            valueGetter: (params) => params?.value && parseJSON(params.value),
            renderCell: (params) =>
                params?.value && (
                    <Tooltip title={format(new Date(params.value), 'd/MM/yyyy HH:mm')}>
                        <Typography
                            variant="inherit"
                            color="inherit"
                            sx={{
                                overflow: 'hidden',
                                display: 'block',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {format(new Date(params.value), 'd/MM/yyyy HH:mm')}
                        </Typography>
                    </Tooltip>
                ),
            label: 'traits-appentityissafedeletable-deletedat'
        },
        {
            field: 'deletedBy',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            renderCell: (params) => renderUser(params?.value, 'fullName'),
            label: 'traits-appentityissafedeletable-deletedby'
        }
    ],
    dataGridState: {
        columns: {
            columnVisibilityModel: {
                id: false,
                acronym: true,
                name: true,
                description: false,
                businessOpportunityTplCount: true,
                businessOpportunityCount: true,
                createdAt: true,
                createdBy: true,
                updatedAt: false,
                updatedBy: false,
                deletedAt: false,
                deletedBy: false
            },
            orderedFields: [
                'actions',
                'id',
                'acronym',
                'name',
                'description',
                'businessOpportunityTplCount',
                'businessOpportunityCount',
                'createdAt',
                'createdBy',
                'updatedAt',
                'updatedBy',
                'deletedAt',
                'deletedBy'
            ],
            dimensions: {
                actions: { width: 60 },
                id: { width: 100 },
                acronym: { width: 80 },
                name: { width: 150 },
                description: { width: 100 },
                businessOpportunityTplCount: { width: 150 },
                businessOpportunityCount: { width: 100 },
                createdAt: { width: 100 },
                createdBy: { width: 100 },
                updatedAt: { width: 100 },
                updatedBy: { width: 100 },
                deletedAt: { width: 100 },
                deletedBy: { width: 100 }
            }
        },
        sorting: {
            sortModel: [{ field: 'id', sort: 'desc' }]
        },
        toolbarFilters: { includeSafeDeleted: false }
    },
    filterDef: [
        {
            filterType: 'string',
            field: { fieldName: 'acronym', fieldType: 'simple' },
            label: 'entity-workmgmt-businessopportunitytype-acronym'
        },
        {
            filterType: 'string',
            field: { fieldName: 'name', fieldType: 'simple' },
            label: 'traits-appentityisdescribable-name'
        },
        {
            filterType: 'string',
            field: { fieldName: 'description', fieldType: 'simple' },
            label: 'traits-appentityisdescribable-description'
        },
        {
            filterType: 'dateTime',
            field: { fieldName: 'createdAt', fieldType: 'simple' },
            label: 'traits-appentity-createdat'
        },
        {
            filterType: 'reference',
            field: { fieldName: 'createdBy', fieldType: 'simple', outputFieldName: 'createdById' },
            label: 'traits-appentity-createdby',
            renderField: (params) => (
                <UserSelect key="filterBusinessOpportunityType-createdBy" multiple displayMode="fullName" {...params} />
            )
        },
        {
            filterType: 'dateTime',
            field: { fieldName: 'updatedAt', fieldType: 'simple' },
            label: 'traits-appentity-updatedat'
        },
        {
            filterType: 'reference',
            field: { fieldName: 'updatedBy', fieldType: 'simple', outputFieldName: 'updatedById' },
            label: 'traits-appentity-updatedby',
            renderField: (params) => (
                <UserSelect key="filterBusinessOpportunityType-updatedBy" multiple displayMode="fullName" {...params} />
            )
        },
        {
            filterType: 'dateTime',
            field: { fieldName: 'deletedAt', fieldType: 'simple' },
            label: 'traits-appentityissafedeletable-deletedat'
        },
        {
            filterType: 'reference',
            field: { fieldName: 'deletedBy', fieldType: 'simple', outputFieldName: 'deletedById' },
            label: 'traits-appentityissafedeletable-deletedby',
            renderField: (params) => (
                <UserSelect key="filterBusinessOpportunityType-deletedBy" multiple displayMode="fullName" {...params} />
            )
        }
    ],
    quickSearchFilterDef: {
        placeholder: 'ui-comp-quicksearchplaceholder-businessopportunitytype',
        fields: [
            { fieldName: 'acronym', fieldType: 'simple' },
            { fieldName: 'name', fieldType: 'simple' },
            { fieldName: 'description', fieldType: 'simple' }
        ]
    },
    sortingDef: [
        { fieldName: 'id', fieldType: 'simple' },
        { fieldName: 'acronym', fieldType: 'simple' },
        { fieldName: 'name', fieldType: 'simple' },
        { fieldName: 'createdAt', fieldType: 'simple' },
        { fieldName: 'createdBy', fieldType: 'simple' },
        { fieldName: 'updatedAt', fieldType: 'simple' },
        { fieldName: 'updatedBy', fieldType: 'simple' },
        { fieldName: 'deletedAt', fieldType: 'simple' },
        { fieldName: 'deletedBy', fieldType: 'simple' }
    ]
};

export default initialState;
